/**
 * PWA Manager - Handles Progressive Web App functionality
 * Includes mobile optimization, app manifest, and installation prompts
 */

interface PWAInstallPrompt {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface PWAManagerConfig {
  enableInstallPrompt?: boolean;
  enableNotifications?: boolean;
  enableOfflineMode?: boolean;
  installPromptDelay?: number;
  skipWaitingOnUpdate?: boolean;
}

declare global {
  interface Window {
    deferredPrompt?: PWAInstallPrompt;
    gtag?: (command: string, targetId: string, config?: any) => void;
  }
  interface Navigator {
    standalone?: boolean;
  }
}

export class PWAManager {
  private config: Required<PWAManagerConfig>;
  private isInstallable = false;
  private isInstalled = false;
  private installPromptEvent: PWAInstallPrompt | null = null;
  private installPromptCallbacks: Array<(canInstall: boolean) => void> = [];
  private updateCallbacks: Array<(registration: ServiceWorkerRegistration) => void> = [];

  constructor(config: PWAManagerConfig = {}) {
    this.config = {
      enableInstallPrompt: true,
      enableNotifications: true,
      enableOfflineMode: true,
      installPromptDelay: 3000,
      skipWaitingOnUpdate: false,
      ...config
    };

    this.init();
  }

  private async init(): Promise<void> {
    this.detectInstallMode();
    this.setupInstallPrompt();
    this.setupServiceWorkerUpdates();
    this.applyMobileOptimizations();

    if (this.config.enableNotifications) {
      await this.setupNotifications();
    }
  }

  private detectInstallMode(): void {
    // Check if app is already installed
    this.isInstalled = this.isRunningStandalone();
    
    // Check if app meets PWA criteria
    this.isInstallable = this.canInstall();
  }

  private isRunningStandalone(): boolean {
    return (
      window.navigator.standalone === true || // iOS Safari
      window.matchMedia('(display-mode: standalone)').matches || // Android Chrome
      document.referrer.includes('android-app://') || // Android TWA
      window.matchMedia('(display-mode: minimal-ui)').matches
    );
  }

  private canInstall(): boolean {
    return (
      'serviceWorker' in navigator &&
      ('BeforeInstallPromptEvent' in window || 'onbeforeinstallprompt' in window) &&
      !this.isInstalled
    );
  }

  private setupInstallPrompt(): void {
    if (!this.config.enableInstallPrompt || !this.canInstall()) return;

    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.installPromptEvent = e as unknown as PWAInstallPrompt;
      window.deferredPrompt = this.installPromptEvent;
      
      setTimeout(() => {
        this.notifyInstallPromptReady(true);
      }, this.config.installPromptDelay);
    });

    window.addEventListener('appinstalled', () => {
      this.isInstalled = true;
      this.installPromptEvent = null;
      window.deferredPrompt = undefined;
      this.notifyInstallPromptReady(false);
      this.trackInstallEvent('installed');
    });
  }

  private setupServiceWorkerUpdates(): void {
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });

    navigator.serviceWorker.ready.then((registration: any) => {
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            this.notifyUpdateReady(registration);
          }
        });
      });
    });
  }

  private applyMobileOptimizations(): void {
    // Viewport meta tag optimization
    this.optimizeViewport();
    
    // Touch and gesture optimizations
    this.optimizeTouchInteractions();
    
    // Performance optimizations
    this.optimizePerformance();
    
    // iOS specific optimizations
    this.optimizeForIOS();
    
    // Android specific optimizations
    this.optimizeForAndroid();
  }

  private optimizeViewport(): void {
    let viewportMeta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
    
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      document.head.appendChild(viewportMeta);
    }

    viewportMeta.content = 'width=device-width, initial-scale=1.0, user-scalable=yes, viewport-fit=cover';
  }

  private optimizeTouchInteractions(): void {
    // Prevent zoom on double-tap for better UX
    document.addEventListener('gesturestart', (e) => e.preventDefault());
    document.addEventListener('gesturechange', (e) => e.preventDefault());
    document.addEventListener('gestureend', (e) => e.preventDefault());

    // Add touch-action CSS property to body
    document.body.style.touchAction = 'manipulation';

    // Optimize scroll behavior
    (document.documentElement.style as any).webkitOverflowScrolling = 'touch';
    (document.documentElement.style as any).overflowScrolling = 'touch';
  }

  private optimizePerformance(): void {
    // Preload critical resources
    this.preloadCriticalResources();
    
    // Optimize images for mobile
    this.optimizeImages();
    
    // Implement resource hints
    this.addResourceHints();
  }

  private optimizeForIOS(): void {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) return;

    // Add iOS specific meta tags
    this.addIOSMetaTags();
    
    // Handle iOS safe areas
    this.handleIOSSafeAreas();
    
    // Optimize for iOS Safari
    this.optimizeForIOSSafari();
  }

  private addIOSMetaTags(): void {
    const metaTags = [
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
      { name: 'apple-mobile-web-app-title', content: document.title }
    ];

    metaTags.forEach(({ name, content }) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    });
  }

  private handleIOSSafeAreas(): void {
    const css = `
      :root {
        --safe-area-inset-top: env(safe-area-inset-top);
        --safe-area-inset-right: env(safe-area-inset-right);
        --safe-area-inset-bottom: env(safe-area-inset-bottom);
        --safe-area-inset-left: env(safe-area-inset-left);
      }
      
      .ios-safe-area-top {
        padding-top: var(--safe-area-inset-top);
      }
      
      .ios-safe-area-bottom {
        padding-bottom: var(--safe-area-inset-bottom);
      }
    `;
    
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);
  }

  private optimizeForIOSSafari(): void {
    // Prevent iOS Safari from adding margins
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    
    // Handle iOS Safari bottom toolbar
    window.addEventListener('resize', this.handleIOSResize.bind(this));
  }

  private handleIOSResize(): void {
    // Handle iOS Safari address bar hide/show
    const viewport = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
    if (viewport) {
      viewport.content = viewport.content.replace(/height=[^,]*,?/, '');
      viewport.content += `, height=${window.innerHeight}`;
    }
  }

  private optimizeForAndroid(): void {
    const isAndroid = /Android/.test(navigator.userAgent);
    if (!isAndroid) return;

    // Add Android specific theme
    this.addAndroidTheme();
    
    // Optimize for Android Chrome
    this.optimizeForAndroidChrome();
  }

  private addAndroidTheme(): void {
    let themeColor = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
    if (!themeColor) {
      themeColor = document.createElement('meta');
      themeColor.name = 'theme-color';
      themeColor.content = '#1976d2'; // Default Material Design blue
      document.head.appendChild(themeColor);
    }
  }

  private optimizeForAndroidChrome(): void {
    // Handle Android Chrome's address bar
    window.addEventListener('scroll', () => {
      if (window.scrollY > 0) {
        document.body.classList.add('scrolling');
      } else {
        document.body.classList.remove('scrolling');
      }
    });
  }

  private preloadCriticalResources(): void {
    // Preload critical CSS and fonts
    const criticalResources = [
      { href: '/static/css/critical.css', as: 'style' },
      { href: '/static/fonts/roboto-v20-latin-regular.woff2', as: 'font', type: 'font/woff2', crossorigin: '' }
    ];

    criticalResources.forEach(({ href, as, type, crossorigin }) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = href;
      link.as = as;
      if (type) link.type = type;
      if (crossorigin !== undefined) link.crossOrigin = crossorigin;
      document.head.appendChild(link);
    });
  }

  private optimizeImages(): void {
    // Implement lazy loading for images
    if ('loading' in HTMLImageElement.prototype) {
      document.querySelectorAll('img').forEach(img => {
        if (!img.loading) {
          img.loading = 'lazy';
        }
      });
    }

    // Add responsive image optimizations
    const css = `
      img {
        height: auto;
        max-width: 100%;
      }
      
      @media (max-width: 768px) {
        img {
          image-rendering: -webkit-optimize-contrast;
          image-rendering: optimize-contrast;
        }
      }
    `;
    
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);
  }

  private addResourceHints(): void {
    const hints = [
      { rel: 'dns-prefetch', href: '//api.example.com' },
      { rel: 'preconnect', href: '//fonts.googleapis.com' },
      { rel: 'preconnect', href: '//fonts.gstatic.com', crossorigin: '' }
    ];

    hints.forEach(({ rel, href, crossorigin }) => {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      if (crossorigin !== undefined) link.crossOrigin = crossorigin;
      document.head.appendChild(link);
    });
  }

  private async setupNotifications(): Promise<void> {
    if (!('Notification' in window)) return;

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      this.enableNotifications();
    }
  }

  private enableNotifications(): void {
    // Enable push notifications if supported
    if ('PushManager' in window && 'serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration: any) => {
        return registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(
            // Replace with your VAPID public key
            'YOUR_VAPID_PUBLIC_KEY'
          ) as BufferSource
        });
      }).then((subscription: any) => {
        console.log('Push notification subscription:', subscription);
      }).catch((error: any) => {
        console.warn('Push notification subscription failed:', error);
      });
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(new ArrayBuffer(rawData.length));

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  private notifyInstallPromptReady(canInstall: boolean): void {
    this.installPromptCallbacks.forEach(callback => callback(canInstall));
  }

  private notifyUpdateReady(registration: ServiceWorkerRegistration): void {
    this.updateCallbacks.forEach(callback => callback(registration));
  }

  private trackInstallEvent(event: string): void {
    // Analytics tracking
    if ('gtag' in window && window.gtag) {
      window.gtag('event', event, {
        event_category: 'PWA',
        event_label: 'App Install'
      });
    }
  }

  // Public API methods
  
  public async promptInstall(): Promise<boolean> {
    if (!this.installPromptEvent) return false;

    try {
      await this.installPromptEvent.prompt();
      const choice = await this.installPromptEvent.userChoice;
      
      this.trackInstallEvent(choice.outcome === 'accepted' ? 'install_accepted' : 'install_dismissed');
      
      if (choice.outcome === 'accepted') {
        this.installPromptEvent = null;
        return true;
      }
      return false;
    } catch (error) {
      console.error('Install prompt error:', error);
      return false;
    }
  }

  public onInstallPromptReady(callback: (canInstall: boolean) => void): () => void {
    this.installPromptCallbacks.push(callback);
    
    // Immediately call if install is already available
    if (this.installPromptEvent) {
      callback(true);
    }
    
    return () => {
      const index = this.installPromptCallbacks.indexOf(callback);
      if (index > -1) {
        this.installPromptCallbacks.splice(index, 1);
      }
    };
  }

  public onUpdateReady(callback: (registration: ServiceWorkerRegistration) => void): () => void {
    this.updateCallbacks.push(callback);
    
    return () => {
      const index = this.updateCallbacks.indexOf(callback);
      if (index > -1) {
        this.updateCallbacks.splice(index, 1);
      }
    };
  }

  public async applyUpdate(registration: ServiceWorkerRegistration): Promise<void> {
    if (!registration.waiting) return;

    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    
    if (this.config.skipWaitingOnUpdate) {
      window.location.reload();
    }
  }

  public getInstallStatus(): {
    isInstallable: boolean;
    isInstalled: boolean;
    canPromptInstall: boolean;
  } {
    return {
      isInstallable: this.isInstallable,
      isInstalled: this.isInstalled,
      canPromptInstall: !!this.installPromptEvent
    };
  }

  public async shareContent(shareData: ShareData): Promise<boolean> {
    if (!navigator.share) {
      // Fallback to Web Share Target API or custom sharing
      return this.fallbackShare(shareData);
    }

    try {
      await navigator.share(shareData);
      return true;
    } catch (error) {
      console.error('Web Share API error:', error);
      return this.fallbackShare(shareData);
    }
  }

  private fallbackShare(shareData: ShareData): boolean {
    // Implement custom sharing fallback
    const url = shareData.url || window.location.href;
    const text = shareData.text || document.title;
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(`${text} ${url}`);
      return true;
    }
    
    return false;
  }

  public addToHomeScreen(): void {
    // Show custom add to home screen instructions
    const instructions = this.getAddToHomeScreenInstructions();
    console.log('Add to Home Screen instructions:', instructions);
  }

  private getAddToHomeScreenInstructions(): string {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    if (isIOS) {
      return 'Tap the Share button and then "Add to Home Screen"';
    } else if (isAndroid) {
      return 'Tap the menu button and then "Add to Home Screen"';
    } else {
      return 'Use your browser\'s menu to add this app to your home screen';
    }
  }

  public enableFullscreen(): void {
    const elem = document.documentElement;
    
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if ((elem as any).webkitRequestFullscreen) {
      (elem as any).webkitRequestFullscreen();
    } else if ((elem as any).msRequestFullscreen) {
      (elem as any).msRequestFullscreen();
    }
  }

  public exitFullscreen(): void {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      (document as any).msExitFullscreen();
    }
  }
}